import { put, takeLatest, call, select } from "redux-saga/effects";
import {
    GET_REPORTS_LIST_ACTION,
    GET_REPORTS_LOADING,
    GET_REPORTS_SUCCESS,
    GET_REPORTS_ERROR,
    DOWNLOAD_REPORTS_LIST_ACTION,
    DOWNLOAD_REPORTS_LIST_START,
    DOWNLOAD_REPORTS_LIST_COMPLETE,
    CLIENT_ANALYSIS_ACTION,
    CLIENT_ANALYSIS_LOADING,
    CLIENT_ANALYSIS_SUCCESS,
    CLIENT_ANALYSIS_ERROR,
    REVENUE_ANALYSIS_ACTION,
    REVENUE_ANALYSIS_LOADING,
    REVENUE_ANALYSIS_SUCCESS,
    REVENUE_ANALYSIS_ERROR,
    TRANSACTION_ANALYSIS_ACTION,
    TRANSACTION_ANALYSIS_LOADING,
    TRANSACTION_ANALYSIS_SUCCESS,
    TRANSACTION_ANALYSIS_ERROR,
    USER_STATS_ACTION,
    USER_STATS_LOADING,
    USER_STATS_SUCCESS,
    USER_STATS_ERROR,
    SALARY_REVENUE_ANALYSIS_ACTION,
    SALARY_REVENUE_ANALYSIS_LOADING,
    SALARY_REVENUE_ANALYSIS_SUCCESS,
    SALARY_REVENUE_ANALYSIS_ERROR,
    COMPANY_ANALYSIS_ACTION,
    COMPANY_ANALYSIS_LOADING,
    COMPANY_ANALYSIS_SUCCESS,
    COMPANY_ANALYSIS_ERROR,
} from "./types";
import Api from "./api";
import { Constants } from "../../lib/constant";

// Split an interceptor-shaped error response into { detail, fieldErrors }.
// The axios interceptor hands us `{ status, data: { code, message, details } }`
// on error, where `details` is the original 4xx body. We keep any `detail`
// string separate (used for 404-style top-level errors) and expose the rest as
// field-level errors so the component can pin them under the right input.
const splitReportError = (res) => {
    const details = res?.details || {};
    let detail = null;
    const fieldErrors = {};
    Object.entries(details).forEach(([key, value]) => {
        if (key === "detail" && typeof value === "string") {
            detail = value;
            return;
        }
        // { field: ["msg", "msg"] } — the DRF-native shape
        if (Array.isArray(value)) {
            fieldErrors[key] = value.filter(Boolean).map(String);
        } else if (typeof value === "string") {
            fieldErrors[key] = [value];
        }
    });
    if (!detail && !Object.keys(fieldErrors).length) {
        // Fallback to whatever the interceptor summarized so the operator
        // always sees *something* rather than a silent no-op.
        const message = Array.isArray(res?.message) ? res.message.join(" · ") : res?.message;
        detail = message || Constants.ERROR.INTERNAL_SERVER;
    }
    return {
        detail,
        fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : null,
    };
};

// Every analysis saga follows the same three-step shape: dispatch LOADING,
// call the API, dispatch SUCCESS or the parsed ERROR. Factoring here means
// a fix to the error-parsing logic only has to happen in one place.
const analysisSagaFactory = ({ apiMethod, loadingType, successType, errorType }) =>
    function* analysisSaga(action) {
        yield put({ type: loadingType });
        try {
            const res = yield call(apiMethod, action.payload);
            // The interceptor puts real API-level errors on res.message (an
            // array) with the raw body on res.details. Success responses are
            // just the parsed JSON array.
            if (res && !Array.isArray(res) && res.message) {
                yield put({ type: errorType, payload: splitReportError(res) });
                return;
            }
            yield put({ type: successType, payload: res });
        } catch (error) {
            yield put({
                type: errorType,
                payload: { detail: error?.message || Constants.ERROR.INTERNAL_SERVER, fieldErrors: null },
            });
        }
    };

const getClientAnalysisSaga = analysisSagaFactory({
    apiMethod: Api.getClientAnalysis,
    loadingType: CLIENT_ANALYSIS_LOADING,
    successType: CLIENT_ANALYSIS_SUCCESS,
    errorType: CLIENT_ANALYSIS_ERROR,
});
const getRevenueAnalysisSaga = analysisSagaFactory({
    apiMethod: Api.getRevenueAnalysis,
    loadingType: REVENUE_ANALYSIS_LOADING,
    successType: REVENUE_ANALYSIS_SUCCESS,
    errorType: REVENUE_ANALYSIS_ERROR,
});
const getTransactionAnalysisSaga = analysisSagaFactory({
    apiMethod: Api.getTransactionAnalysis,
    loadingType: TRANSACTION_ANALYSIS_LOADING,
    successType: TRANSACTION_ANALYSIS_SUCCESS,
    errorType: TRANSACTION_ANALYSIS_ERROR,
});
const getUserStatsSaga = analysisSagaFactory({
    apiMethod: Api.getUserStats,
    loadingType: USER_STATS_LOADING,
    successType: USER_STATS_SUCCESS,
    errorType: USER_STATS_ERROR,
});
const getSalaryRevenueAnalysisSaga = analysisSagaFactory({
    apiMethod: Api.getSalaryRevenueAnalysis,
    loadingType: SALARY_REVENUE_ANALYSIS_LOADING,
    successType: SALARY_REVENUE_ANALYSIS_SUCCESS,
    errorType: SALARY_REVENUE_ANALYSIS_ERROR,
});
const getCompanyAnalysisSaga = analysisSagaFactory({
    apiMethod: Api.getCompanyAnalysis,
    loadingType: COMPANY_ANALYSIS_LOADING,
    successType: COMPANY_ANALYSIS_SUCCESS,
    errorType: COMPANY_ANALYSIS_ERROR,
});



function* getReportsListSaga(action) {
    yield put({ type: GET_REPORTS_LOADING, payload: action.payload });
    try {
        const filters = yield select((state) => state.reportsReducer.filters);
        let res = yield call(Api.getReportsList, filters);
        if (res?.data?.summaries?.length) 
            yield put({ type: GET_REPORTS_SUCCESS, payload: res.data.summaries });
        else
            yield put({
                type: GET_REPORTS_ERROR,
                payload: [Constants.ERROR.INTERNAL_SERVER],
            });
    } catch (error) {
        yield put({ type: GET_REPORTS_ERROR, payload: error.message });
    }
}

function* downloadReportsListSaga(action) {
    const { data, fileName } = action.payload;

    try {
        yield put({ type: DOWNLOAD_REPORTS_LIST_START });

         const transformedData = data?.data?.map((item) => ({
           employee_id: item.employee_id,
           first_name: item.first_name,
           last_name: item.last_name,
           total_amount: parseFloat(item.total_amount) || 0,
         }));
        
        const newData = {data: transformedData, fileName: data?.fileName}
        
        
        yield call(Api.downloadReportsList, newData, fileName)
    } catch(error) {
        console.error("Error in downloadReportsListSaga:", error);
    } finally {

        yield put({ type: DOWNLOAD_REPORTS_LIST_COMPLETE });

    }

}


function* getDownloadReportsListSaga(action) {
    yield put({ type: DOWNLOAD_REPORTS_LIST_START, payload: action.payload });
    try {
        const filters = yield select((state) => state.reportsReducer.filters);
        let res = yield call(Api.downloadAdminReportsList, filters);
        if (!res.message)
        {
            console.log(res)
            yield put({ type: DOWNLOAD_REPORTS_LIST_COMPLETE, payload: null });
        }
        else
            yield put({
                type: GET_REPORTS_ERROR,
                payload: [Constants.ERROR.INTERNAL_SERVER, res.message],
            });
    } catch (error) {
        yield put({ type: GET_REPORTS_ERROR, payload: error.message });
    }
}


function* watchReportsAsync() {
    yield takeLatest(GET_REPORTS_LIST_ACTION, getReportsListSaga);
    yield takeLatest(DOWNLOAD_REPORTS_LIST_ACTION, getDownloadReportsListSaga);
    yield takeLatest(CLIENT_ANALYSIS_ACTION, getClientAnalysisSaga);
    yield takeLatest(REVENUE_ANALYSIS_ACTION, getRevenueAnalysisSaga);
    yield takeLatest(TRANSACTION_ANALYSIS_ACTION, getTransactionAnalysisSaga);
    yield takeLatest(USER_STATS_ACTION, getUserStatsSaga);
    yield takeLatest(SALARY_REVENUE_ANALYSIS_ACTION, getSalaryRevenueAnalysisSaga);
    yield takeLatest(COMPANY_ANALYSIS_ACTION, getCompanyAnalysisSaga);
}

export default ( watchReportsAsync);